import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

const emptyForm = {
  name: "",
  price: "",
  session_duration: "",
  edited_images: "",
  extra: "",
  is_active: 1,
};

export default function StudioPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const sortedPackages = useMemo(() => {
    return [...packages].sort((a, b) => Number(b.id) - Number(a.id));
  }, [packages]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  const getErrorMessage = (err, fallback) => {
    if (err.message === "REQUEST_TIMEOUT") {
      return "Request timed out. Please try again.";
    }
    if (err.message === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
      return "Your session has expired. Please sign in again.";
    }
    return err.message || fallback;
  };

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_studio_packages",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load studio packages.");
      }

      setPackages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load studio packages."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || "",
      price: item.price ?? "",
      session_duration: item.session_duration || "",
      edited_images: item.edited_images || "",
      extra: item.extra || "",
      is_active: Number(item.is_active) === 1 ? 1 : 0,
    });
    setError("");
    setSuccess("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleChange = (key) => (e) => {
    const value = key === "is_active" ? Number(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Package name is required.";
    if (form.price === "" || Number(form.price) <= 0)
      return "Enter a valid price.";
    if (!form.session_duration.trim()) return "Session duration is required.";
    if (form.edited_images === "" || Number(form.edited_images) < 0)
      return "Enter a valid number of edited images.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        session_duration: form.session_duration.trim(),
        edited_images: Number(form.edited_images),
        extra: form.extra.trim(),
        is_active: Number(form.is_active),
      };

      const res = await adminApiRequest({
        script: editingItem ? "update_studio_package" : "create_studio_package",
        data: editingItem ? { id: editingItem.id, ...payload } : payload,
      });

      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to ${editingItem ? "update" : "create"} studio package.`,
        );
      }

      setSuccess(
        editingItem
          ? "Studio package updated successfully."
          : "Studio package created successfully.",
      );

      closeModal();
      await loadPackages();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          `Unable to ${editingItem ? "update" : "create"} studio package.`,
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const nextStatus = Number(item.is_active) === 1 ? 0 : 1;

    try {
      setError("");
      setSuccess("");

      const res = await adminApiRequest({
        script: "toggle_studio_package_status",
        data: {
          id: item.id,
          is_active: nextStatus,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update package status.");
      }

      setSuccess(
        nextStatus === 1
          ? "Studio package activated successfully."
          : "Studio package deactivated successfully.",
      );

      setPackages((prev) =>
        prev.map((pkg) =>
          Number(pkg.id) === Number(item.id)
            ? { ...pkg, is_active: nextStatus }
            : pkg,
        ),
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update package status."));
    }
  };

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Studio Packages
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Manage studio session packages, pricing, and visibility.
            </p>
          </div>

          <button
            onClick={openCreateModal}
            className="rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            + Add Studio Package
          </button>
        </div>

        {(error || success) && (
          <div className="px-5 pt-4">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading studio packages...
            </div>
          ) : sortedPackages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">
                No studio packages added yet.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Package</th>
                      <th className="px-4 py-3 font-semibold">
                        Session Duration
                      </th>
                      <th className="px-4 py-3 font-semibold">Edited Images</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedPackages.map((item) => (
                      <tr key={item.id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-900">
                            {item.name}
                          </div>
                          {item.extra && (
                            <div className="mt-1 max-w-md text-sm text-slate-500">
                              {item.extra}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.session_duration}
                        </td>

                        <td className="px-4 py-4 text-sm text-slate-700">
                          {item.edited_images}
                        </td>

                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          £{Number(item.price).toFixed(2)}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                              Number(item.is_active) === 1
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {Number(item.is_active) === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(item)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                                Number(item.is_active) === 1
                                  ? "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                              }`}
                            >
                              {Number(item.is_active) === 1
                                ? "Deactivate"
                                : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingItem ? "Edit Studio Package" : "Add Studio Package"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Fill in the package details below.
                </p>
              </div>

              <button
                onClick={closeModal}
                className="rounded-lg px-3 py-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. Package 1"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={handleChange("price")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 200"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Session Duration
                  </label>
                  <input
                    type="text"
                    value={form.session_duration}
                    onChange={handleChange("session_duration")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 1 Hour"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Number of Edited Images
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.edited_images}
                    onChange={handleChange("edited_images")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder="e.g. 5"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Extra
                  </label>
                  <input
                    type="text"
                    value={form.extra}
                    onChange={handleChange("extra")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                    placeholder='e.g. Plus 16"x20" photo frame'
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Status
                  </label>
                  <select
                    value={form.is_active}
                    onChange={handleChange("is_active")}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-slate-900"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? editingItem
                      ? "Updating..."
                      : "Saving..."
                    : editingItem
                      ? "Update Package"
                      : "Create Package"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
